/* ==========================================================================
   The Natural Route — theme behaviour
   No dependencies. Every module is guarded by a data-flag so it can be re-run
   safely after the theme editor swaps a section, or after the cart drawer
   re-renders itself from the Section Rendering API.
   ========================================================================== */

(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function once(el, flag) {
    if (!el || el.dataset[flag] === 'true') return false;
    el.dataset[flag] = 'true';
    return true;
  }

  function trapFocus(container, event) {
    var focusable = $$(FOCUSABLE, container).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function lockScroll(locked) {
    document.body.classList.toggle('is-locked', locked);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* --- Sticky header ---------------------------------------------------- */

  function initStickyHeader() {
    var header = $('[data-tnr-header]');
    if (!once(header, 'tnrSticky')) return;

    var ticking = false;

    function update() {
      header.classList.toggle('is-stuck', window.scrollY > 8);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* --- Mega menu -------------------------------------------------------- */

  function initMegaMenu() {
    var items = $$('[data-tnr-mega-item]').filter(function (item) {
      return once(item, 'tnrMega');
    });
    if (!items.length) return;

    var backdrop = $('[data-tnr-mega-backdrop]');
    var openItem = null;
    var closeTimer = null;

    function setOpen(item, open) {
      var trigger = $('[data-tnr-mega-trigger]', item);
      item.setAttribute('data-tnr-open', open ? 'true' : 'false');
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function close(item) {
      if (!item) return;
      setOpen(item, false);
      if (openItem === item) openItem = null;
      if (!openItem && backdrop) backdrop.classList.remove('is-visible');
    }

    function open(item) {
      window.clearTimeout(closeTimer);
      if (openItem && openItem !== item) setOpen(openItem, false);
      openItem = item;
      setOpen(item, true);
      if (backdrop) backdrop.classList.add('is-visible');
    }

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        open(item);
      });

      item.addEventListener('mouseleave', function () {
        closeTimer = window.setTimeout(function () {
          close(item);
        }, 140);
      });

      // Keyboard: opening on focus keeps the panel reachable by Tab alone.
      item.addEventListener('focusin', function () {
        open(item);
      });

      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close(item);
      });

      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          close(item);
          var trigger = $('[data-tnr-mega-trigger]', item);
          if (trigger) trigger.focus();
        }
      });
    });

    if (backdrop && once(backdrop, 'tnrMegaBackdrop')) {
      backdrop.addEventListener('mouseenter', function () {
        close(openItem);
      });
    }
  }

  /* --- Drawer accordions ------------------------------------------------ */

  function initAccordions() {
    $$('[data-tnr-accordion]').forEach(function (toggle) {
      if (!once(toggle, 'tnrAccordion')) return;

      toggle.addEventListener('click', function () {
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });
  }

  /* --- Navigation drawer ------------------------------------------------ */

  function initDrawer() {
    var drawer = $('[data-tnr-drawer]');
    var toggle = $('[data-tnr-drawer-open]');
    if (!drawer || !toggle || !once(drawer, 'tnrDrawer')) return;

    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.removeAttribute('aria-hidden');
      toggle.setAttribute('aria-expanded', 'true');
      lockScroll(true);

      var target = $('[data-tnr-drawer-close]', drawer) || drawer;
      window.setTimeout(function () {
        target.focus();
      }, 60);
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    toggle.addEventListener('click', open);

    drawer.addEventListener('click', function (event) {
      if (
        event.target.closest('[data-tnr-drawer-close]') ||
        event.target.hasAttribute('data-tnr-drawer-overlay')
      ) {
        close();
      }
    });

    drawer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab' && drawer.classList.contains('is-open')) {
        trapFocus(drawer, event);
      }
    });

    window.addEventListener('resize', function () {
      if (window.innerWidth > 990 && drawer.classList.contains('is-open')) close();
    });
  }

  /* --- Cart drawer + AJAX cart ------------------------------------------ */

  var Cart = (function () {
    var SECTION = 'cart-drawer';
    var lastFocused = null;

    function drawer() {
      return $('[data-tnr-cart-drawer]');
    }

    function isEnabled() {
      return !!drawer();
    }

    function open() {
      var el = drawer();
      if (!el) return;
      lastFocused = document.activeElement;
      el.classList.add('is-open');
      el.removeAttribute('aria-hidden');
      lockScroll(true);
      window.setTimeout(function () {
        var close = $('[data-tnr-cart-close]', el);
        if (close) close.focus();
      }, 60);
    }

    function close() {
      var el = drawer();
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function setLoading(loading) {
      var el = drawer();
      if (el) el.classList.toggle('is-loading', loading);
    }

    function updateCount(count) {
      $$('[data-tnr-cart-count]').forEach(function (badge) {
        badge.textContent = count;
        badge.hidden = count === 0;
      });
      var drawerCount = $('[data-tnr-cart-drawer-count]');
      if (drawerCount) drawerCount.textContent = '(' + count + ')';
    }

    // Swap only the contents wrapper so the drawer chrome never flickers.
    function applySection(html) {
      var el = drawer();
      if (!el || !html) return;

      var parsed = document.createElement('div');
      parsed.innerHTML = html;

      var fresh = $('[data-tnr-cart-contents]', parsed);
      var current = $('[data-tnr-cart-contents]', el);
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        init();
        if (typeof window.initQuantity === 'function') {
          window.initQuantity();
        } else if (typeof initQuantity === 'function') {
          initQuantity();
        }
      }
    }

    function handleResponse(data) {
      if (!data) return;
      if (typeof data.item_count === 'number') updateCount(data.item_count);
      if (data.sections && data.sections[SECTION]) {
        applySection(data.sections[SECTION]);
      } else {
        refresh();
      }
    }

    // Fallback when the endpoint did not echo sections back.
    function refresh() {
      return fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + '?sections=' + SECTION
        : '/?sections=' + SECTION)
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          applySection(data[SECTION]);
        })
        .catch(function () {
          /* leave the drawer as-is; the cart page remains authoritative */
        });
    }

    function post(url, body) {
      setLoading(true);
      body.sections = SECTION;

      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          handleResponse(data);
          return data;
        })
        .catch(function () {
          // Hard-navigate rather than silently losing the customer's action.
          window.location.href = '/cart';
        })
        .finally(function () {
          setLoading(false);
        });
    }

    function addFromForm(form) {
      var formData = new FormData(form);
      var body = {};
      formData.forEach(function (value, key) {
        body[key] = value;
      });

      // Button feedback: show spinner while adding
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('tnr-button--adding');
        submitBtn.classList.remove('tnr-button--added');
      }

      setLoading(true);
      var params = new URLSearchParams();
      formData.forEach(function (value, key) {
        params.append(key, value);
      });
      params.append('sections', SECTION);

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: params
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function () {
          return refresh();
        })
        .then(function () {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          updateCount(cart.item_count);
          // Button feedback: show "Added ✓" briefly before opening drawer
          if (submitBtn) {
            submitBtn.classList.remove('tnr-button--adding');
            submitBtn.classList.add('tnr-button--added');
          }
          window.setTimeout(function () {
            open();
            // Announce cart addition to screen readers (H11)
            if (typeof window.tnrAnnounceCart === 'function') {
              window.tnrAnnounceCart('Item added to cart.');
            }
            // Reset button after drawer is open
            window.setTimeout(function () {
              if (submitBtn) submitBtn.classList.remove('tnr-button--added');
            }, 600);
          }, 500);
        })
        .catch(function () {
          if (submitBtn) {
            submitBtn.classList.remove('tnr-button--adding');
            submitBtn.classList.remove('tnr-button--added');
          }
          form.submit();
        })
        .finally(function () {
          setLoading(false);
        });
    }

    function changeLine(line, quantity) {
      // Animate removal when quantity becomes 0
      if (quantity === 0) {
        var el = drawer();
        if (el) {
          var lines = $$('[data-tnr-cart-line]', el);
          var lineEl = lines[line - 1];
          if (lineEl) {
            lineEl.classList.add('tnr-cart-line--removing');
          }
        }
        // Let the animation play before sending the request
        return new Promise(function (resolve) {
          window.setTimeout(function () {
            resolve(post('/cart/change.js', { line: line, quantity: 0 }));
          }, 300);
        });
      }
      return post('/cart/change.js', { line: line, quantity: quantity });
    }

    function init() {
      var el = drawer();
      if (el && once(el, 'tnrCartDrawer')) {
        el.addEventListener('click', function (event) {
          if (event.target.closest('[data-tnr-cart-close]')) close();
        });

        el.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
          } else if (event.key === 'Tab' && el.classList.contains('is-open')) {
            trapFocus(el, event);
          }
        });
      }

      // Header cart icon opens the drawer instead of navigating.
      $$('[data-tnr-cart-open]').forEach(function (trigger) {
        if (!once(trigger, 'tnrCartOpen')) return;
        trigger.addEventListener('click', function (event) {
          if (!isEnabled()) return;
          event.preventDefault();
          open();
        });
      });

      // Quantity inputs inside the drawer.
      $$('[data-tnr-cart-qty]').forEach(function (input) {
        if (!once(input, 'tnrCartQty')) return;
        input.addEventListener('change', function () {
          var widget = input.closest('[data-tnr-line]');
          if (!widget) return;
          var line = parseInt(widget.dataset.tnrLine, 10);
          var qty = parseInt(input.value, 10);
          if (isNaN(line) || isNaN(qty)) return;
          changeLine(line, Math.max(0, qty));
        });
      });

      $$('[data-tnr-cart-remove]').forEach(function (button) {
        if (!once(button, 'tnrCartRemoveBound')) return;
        button.addEventListener('click', function () {
          var line = parseInt(button.dataset.tnrCartRemove, 10);
          if (!isNaN(line)) changeLine(line, 0);
        });
      });

      var note = $('[data-tnr-cart-note]');
      if (note && once(note, 'tnrCartNote')) {
        var noteTimer = null;
        note.addEventListener('input', function () {
          window.clearTimeout(noteTimer);
          noteTimer = window.setTimeout(function () {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: note.value })
            });
          }, 600);
        });
      }
    }

    function initForms() {
      $$('form[action$="/cart/add"], form[action*="/cart/add"]').forEach(function (form) {
        if (!once(form, 'tnrCartForm')) return;
        form.addEventListener('submit', function (event) {
          if (!isEnabled()) return;
          event.preventDefault();
          addFromForm(form);
        });
      });
    }

    /* --- Cart page: AJAX quantity + remove ------------------------------ */

    function initCartPage() {
      var wrapper = $('[data-tnr-cart-page]');
      if (!wrapper || !once(wrapper, 'tnrCartPage')) return;

      function setPageLoading(loading) {
        wrapper.classList.toggle('is-loading', loading);
      }

      function reloadCartPage() {
        return fetch(window.location.pathname, {
          headers: { Accept: 'text/html' }
        })
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-tnr-cart-page]');
            if (fresh) {
              wrapper.innerHTML = fresh.innerHTML;
              // Re-init so event listeners bind to new DOM
              initCartPage();
              initQuantity();
            }
            // Update header cart count
            var countEl = doc.querySelector('[data-tnr-cart-count]');
            if (countEl) {
              $$('[data-tnr-cart-count]').forEach(function (badge) {
                badge.textContent = countEl.textContent;
                badge.hidden = countEl.hidden;
              });
            }
          })
          .catch(function () {
            window.location.reload();
          })
          .finally(function () {
            setPageLoading(false);
          });
      }

      function cartPageChange(line, quantity) {
        setPageLoading(true);

        // Animate removal
        if (quantity === 0) {
          var items = $$('[data-tnr-cart-item]', wrapper);
          var itemEl = items[line - 1];
          if (itemEl) {
            itemEl.classList.add('tnr-cart-item--removing');
          }
          return new Promise(function (resolve) {
            window.setTimeout(function () {
              resolve(
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ line: line, quantity: 0 })
                }).then(function () { return reloadCartPage(); })
              );
            }, 300);
          });
        }

        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line, quantity: quantity })
        }).then(function () { return reloadCartPage(); });
      }

      // Intercept quantity stepper clicks on cart page
      $$('[data-tnr-cart-item]', wrapper).forEach(function (item, index) {
        var lineNum = index + 1;
        var input = $('input[name="updates[]"]', item);

        // Handle direct input changes (also triggered by initQuantity)
        if (input) {
          input.addEventListener('change', function () {
            var qty = parseInt(input.value, 10);
            if (isNaN(qty) || qty < 0) return;
            cartPageChange(lineNum, qty);
          });
        }
      });

      // Remove buttons
      $$('[data-tnr-cart-page-remove]', wrapper).forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          var line = parseInt(button.dataset.tnrCartPageRemove, 10);
          if (!isNaN(line)) cartPageChange(line, 0);
        });
      });
    }

    return { init: init, initForms: initForms, initCartPage: initCartPage, open: open, close: close };
  })();

  /* --- Predictive search ------------------------------------------------ */

  function initPredictiveSearch() {
    $$('[data-tnr-search]').forEach(function (root) {
      if (!once(root, 'tnrSearch')) return;

      var input = $('[data-tnr-search-input]', root);
      var panel = $('[data-tnr-search-panel]', root);
      if (!input || !panel) return;

      var resultsWrap = $('[data-tnr-search-results-wrap]', panel);
      var loading = $('[data-tnr-search-loading]', panel);
      var status = $('[data-tnr-search-status]', panel);
      var all = $('[data-tnr-search-all]', panel);
      var timer = null;
      var controller = null;

      function hide() {
        root.classList.remove('is-open');
        input.setAttribute('aria-expanded', 'false');
      }

      function show() {
        root.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
      }

      function search(term) {
        if (controller) controller.abort();
        controller = new AbortController();

        if (loading) loading.hidden = false;

        // Rendered server-side through the Section Rendering API so prices,
        // currency and translations come from Liquid rather than being
        // re-formatted in JS.
        var url =
          '/search/suggest?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6&resources[options][fields]=title,variants.title,vendor&section_id=predictive-search';

        fetch(url, { signal: controller.signal })
          .then(function (res) {
            return res.text();
          })
          .then(function (html) {
            var parsed = document.createElement('div');
            parsed.innerHTML = html;

            var fresh = $('[data-tnr-search-results-wrap]', parsed);
            if (fresh && resultsWrap) {
              resultsWrap.innerHTML = fresh.innerHTML;
            }

            var count = $$('[data-tnr-search-result]', resultsWrap).length;
            if (status) {
              status.textContent = count
                ? count + ' results for ' + term
                : 'No products matched ' + term;
            }
            if (all) {
              all.hidden = count === 0;
              all.href = '/search?q=' + encodeURIComponent(term);
            }
            if (loading) loading.hidden = true;
            show();
          })
          .catch(function (error) {
            if (error && error.name === 'AbortError') return;
            if (loading) loading.hidden = true;
            hide();
          });
      }

      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-autocomplete', 'list');

      input.addEventListener('input', function () {
        var term = input.value.trim();
        window.clearTimeout(timer);

        if (term.length < 2) {
          hide();
          return;
        }

        timer = window.setTimeout(function () {
          search(term);
        }, 220);
      });

      input.addEventListener('focus', function () {
        if (input.value.trim().length >= 2 && $$('[data-tnr-search-result]', panel).length) show();
      });

      root.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          hide();
          input.focus();
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        var results = $$('[data-tnr-search-result]', panel);
        if (!results.length) return;

        event.preventDefault();
        var index = results.indexOf(document.activeElement);
        var next =
          event.key === 'ArrowDown'
            ? Math.min(results.length - 1, index + 1)
            : Math.max(0, index - 1);

        if (index === -1 && event.key === 'ArrowDown') next = 0;
        results[next].focus();
      });

      document.addEventListener('click', function (event) {
        if (!root.contains(event.target)) hide();
      });
    });
  }

  /* --- Sticky add-to-cart bar ------------------------------------------- */

  function initStickyAtc() {
    var bar = $('[data-tnr-sticky-bar]');
    var anchor = $('[data-tnr-sticky-anchor]');
    if (!bar || !anchor || !once(bar, 'tnrStickyBar')) return;

    if (!('IntersectionObserver' in window)) {
      bar.remove();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Show once the real add-to-cart has scrolled out of view upward.
          var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', passed);
        });
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
  }

  /* --- Before / after comparison ---------------------------------------- */

  function initCompare() {
    $$('[data-tnr-compare]').forEach(function (root) {
      if (!once(root, 'tnrCompare')) return;

      var range = $('[data-tnr-compare-range]', root);
      if (!range) return;

      function apply() {
        root.style.setProperty('--compare-pos', range.value + '%');
      }

      range.addEventListener('input', apply);
      range.addEventListener('change', apply);
      apply();
    });
  }

  /* --- Scroll reveal ---------------------------------------------------- */

  function initReveal() {
    if (document.body.classList.contains('tnr-no-anim') || prefersReducedMotion()) return;

    var targets = $$('[data-tnr-reveal]').filter(function (el) {
      return once(el, 'tnrReveal');
    });
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      // Stagger containers animate their children, so the container itself
      // must not be transformed — that would fight scroll-snap on carousels.
      // Either way the hidden state is only applied once JS is running.
      if (el.classList.contains('tnr-reveal--stagger')) {
        el.classList.add('tnr-reveal-ready');
      } else {
        el.classList.add('tnr-reveal');
      }
      observer.observe(el);
    });
  }

  /* --- Discount popup --------------------------------------------------- */

  function initPopup() {
    var popup = $('[data-tnr-popup]');
    if (!once(popup, 'tnrPopup')) return;

    var storageKey = 'tnr:popup-subscribed';
    var delay = parseInt(popup.dataset.tnrPopupDelay, 10) || 4000;
    var lastFocused = null;
    var subscribed = false;

    try {
      subscribed = window.localStorage.getItem(storageKey) === 'true';
    } catch (error) {
      subscribed = false;
    }

    var designMode = window.Shopify && window.Shopify.designMode;
    // Only skip if the user has already subscribed (not just dismissed)
    if (subscribed && !designMode) return;

    function open() {
      lastFocused = document.activeElement;
      popup.classList.add('is-open');
      popup.removeAttribute('aria-hidden');
      lockScroll(true);
      var field = $('input[type="email"]', popup);
      if (field) field.focus();
    }

    function close() {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      // NOTE: We do NOT set localStorage here — closing without subscribing
      // means the popup will show again on the next visit.
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    // Mark as subscribed when the form is submitted so next visit skips the popup.
    var form = $('form', popup);
    if (form) {
      form.addEventListener('submit', function () {
        try {
          window.localStorage.setItem(storageKey, 'true');
        } catch (error) {
          /* storage unavailable */
        }
      });
    }

    popup.addEventListener('click', function (event) {
      if (
        event.target.closest('[data-tnr-popup-close]') ||
        event.target.hasAttribute('data-tnr-popup-overlay')
      ) {
        close();
      }
    });

    popup.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab' && popup.classList.contains('is-open')) {
        trapFocus(popup, event);
      }
    });

    window.setTimeout(open, delay);
  }

  /* --- Carousel --------------------------------------------------------- */

  function initCarousels() {
    $$('[data-tnr-carousel]').forEach(function (carousel) {
      if (!once(carousel, 'tnrCarousel')) return;

      var track = $('[data-tnr-carousel-track]', carousel);
      var prev = $('[data-tnr-carousel-prev]', carousel);
      var next = $('[data-tnr-carousel-next]', carousel);
      if (!track) return;

      function step() {
        var card = track.firstElementChild;
        if (!card) return track.clientWidth;
        var gap = parseFloat(window.getComputedStyle(track).columnGap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function sync() {
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        var overflows = maxScroll > 0;

        if (prev) prev.disabled = !overflows || track.scrollLeft <= 0;
        if (next) next.disabled = !overflows || track.scrollLeft >= maxScroll;
        carousel.classList.toggle('is-static', !overflows);
      }

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* --- Quantity steppers ------------------------------------------------ */

  function initQuantity() {
    $$('[data-tnr-quantity]').forEach(function (widget) {
      if (!once(widget, 'tnrQuantity')) return;

      var input = $('input', widget);
      if (!input) return;

      var autoSubmit = widget.hasAttribute('data-tnr-quantity-submit');

      function change(delta) {
        var min = parseInt(input.getAttribute('min'), 10);
        if (isNaN(min)) min = 0;

        var current = parseInt(input.value, 10);
        if (isNaN(current)) current = min;

        var value = Math.max(min, current + delta);
        if (value === current) return;

        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        if (!autoSubmit || !input.form) return;

        // The cart form needs the `update` button to act as the submitter,
        // so click it rather than submitting the form bare.
        var submitter = input.form.querySelector('[name="update"]');
        if (submitter) {
          submitter.click();
        } else if (input.form.requestSubmit) {
          input.form.requestSubmit();
        } else {
          input.form.submit();
        }
      }

      $$('[data-tnr-quantity-step]', widget).forEach(function (button) {
        button.addEventListener('click', function () {
          change(parseInt(button.dataset.tnrQuantityStep, 10) || 0);
        });
      });
    });
  }

  /* --- Product gallery -------------------------------------------------- */

  function initGalleries() {
    $$('[data-tnr-gallery]').forEach(function (gallery) {
      if (!once(gallery, 'tnrGallery')) return;

      var main = $('[data-tnr-gallery-main]', gallery);
      var thumbs = $$('[data-tnr-gallery-thumb]', gallery);
      if (!main || thumbs.length < 2) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.dataset.tnrGalleryThumb;
          if (!src) return;

          main.src = src;
          var img = thumb.querySelector('img');
          if (img) main.alt = img.alt;

          thumbs.forEach(function (other) {
            var active = other === thumb;
            other.classList.toggle('is-active', active);
            // Accessibility (H9): only set aria-current when true;
            // removing the attribute (not setting to "false") for inactive thumbs.
            if (active) {
              other.setAttribute('aria-current', 'true');
            } else {
              other.removeAttribute('aria-current');
            }
          });

          // Accessibility (L6): announce the new image to screen reader users
          // via the live region injected into main-product.liquid.
          var statusEl = document.getElementById('TnrGalleryStatus');
          if (statusEl && img && img.alt) {
            statusEl.textContent = '';
            window.setTimeout(function () {
              statusEl.textContent = 'Now showing: ' + img.alt;
            }, 50);
          }
        });
      });
    });
  }

  /* --- Auto-submitting selects ------------------------------------------ */

  function initSortForms() {
    $$('[data-tnr-auto-submit]').forEach(function (field) {
      if (!once(field, 'tnrAutoSubmit')) return;

      field.addEventListener('change', function () {
        if (field.form) field.form.submit();
      });
    });
  }

  /* --- Mobile search overlay -------------------------------------------- */
  /* Accessibility: when opened, trap focus inside the overlay panel;
     when closed, return focus to the button that triggered it.
     WCAG SC 2.1.2 (No Keyboard Trap) and SC 2.4.3 (Focus Order). */

  function initMobileSearch() {
    var overlay = $('[data-tnr-mobile-search]');
    var openTrigger = $('[data-tnr-mobile-search-open]');
    var closeTrigger = $('[data-tnr-mobile-search-close]');
    var input = $('[data-tnr-mobile-search-input]');
    if (!overlay || !openTrigger) return;
    if (!once(overlay, 'tnrMobileSearch')) return;

    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      overlay.classList.add('is-open');
      overlay.removeAttribute('aria-hidden');
      lockScroll(true);
      if (input) window.setTimeout(function () { input.focus(); }, 80);
    }

    function close() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      // Return focus to the element that opened the overlay (M3).
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openTrigger.addEventListener('click', open);

    overlay.addEventListener('click', function (event) {
      if (
        event.target === overlay ||
        event.target.closest('[data-tnr-mobile-search-close]')
      ) {
        close();
      }
    });

    overlay.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      // Focus trap (M2): prevent Tab from escaping into background content.
      if (event.key === 'Tab' && overlay.classList.contains('is-open')) {
        var panel = $('[data-tnr-mobile-search]');
        trapFocus(panel, event);
      }
    });
  }

  /* --- Cart change announcer -------------------------------------------- */
  /* Accessibility (H11): when AJAX cart updates happen, screen reader users
     need feedback. We inject a polite live region into the document once
     and update its text whenever the cart count changes. */

  function initCartAnnouncer() {
    // Create the live region if it doesn't exist yet.
    var announcer = document.getElementById('TnrCartAnnouncer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'TnrCartAnnouncer';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'visually-hidden';
      document.body.appendChild(announcer);
    }

    // Expose a global so Cart module can call it after AJAX updates.
    window.tnrAnnounceCart = function (message) {
      // Clear then set: forces screen readers to re-announce even identical text.
      announcer.textContent = '';
      window.setTimeout(function () {
        announcer.textContent = message;
      }, 50);
    };
  }

  /* --- Marquee pause/play ------------------------------------------------ */
  /* Accessibility (M1): WCAG 2.2 SC 2.2.2 — auto-scrolling content must be
     pausable. This wires up the pause button injected by tnr-marquee-track. */

  function initMarqueePause() {
    $$('[data-tnr-marquee-pause]').forEach(function (btn) {
      if (!once(btn, 'tnrMarqueePause')) return;
      var marquee = btn.closest('[class*="tnr-marquee"]');
      if (!marquee) return;

      btn.addEventListener('click', function () {
        var paused = marquee.classList.toggle('is-paused');
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? 'Play scrolling marquee' : 'Pause scrolling marquee');
        // Update visually-hidden span text
        var vhSpan = btn.querySelector('.visually-hidden');
        if (vhSpan) vhSpan.textContent = paused ? 'Play marquee' : 'Pause marquee';
      });
    });
  }

  /* --- Variant Selector ------------------------------------------------- */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = '$((amount))'.replace('((', '{{').replace('))', '}}');
    function formatWithDelimiters(number) {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(2);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return dollars + '.' + (parts[1] || '00');
    }
    value = formatWithDelimiters(cents);
    return formatString.replace(placeholderRegex, value);
  }

  function initVariantSelectors() {
    $$('[data-tnr-variant-form]').forEach(function (formContainer) {
      if (!once(formContainer, 'tnrVariantSelector')) return;

      var jsonScript = $('[data-tnr-variants-json]', formContainer.closest('.tnr-product-info'));
      if (!jsonScript) return;

      var variants = JSON.parse(jsonScript.textContent);
      var form = $('form', formContainer);
      if (!form) return;

      var inputs = $$('input[name="id"], select[name="id"]', form);
      if (!inputs.length) return;

      var priceRow = $('[data-tnr-price-row]', formContainer.closest('.tnr-product-info'));
      var priceEl = priceRow ? $('[data-tnr-price]', priceRow) : null;
      var priceCompareEl = priceRow ? $('[data-tnr-price-compare]', priceRow) : null;
      
      var stickyActions = $('[data-tnr-sticky-actions]');
      var stickyPrice = $('[data-tnr-sticky-price]');
      var galleryMain = $('[data-tnr-gallery-main]');

      function update(selectedId) {
        var variant = variants.find(function (v) { return v.id == selectedId; });
        if (!variant) return;

        if (variant.id) {
          window.history.replaceState({ }, '', '?variant=' + variant.id);
        }

        if (priceEl) {
          priceEl.innerHTML = (variant.compare_at_price > variant.price ? '<span class="visually-hidden">Sale price: </span>' : '') + formatMoney(variant.price);
        }
        
        if (priceCompareEl) {
          var saveBadge = $('.tnr-badge', priceRow);
          if (variant.compare_at_price > variant.price) {
            priceCompareEl.innerHTML = '<span class="visually-hidden">Regular price: </span>' + formatMoney(variant.compare_at_price);
            priceCompareEl.style.display = '';
            if (saveBadge) saveBadge.style.display = '';
          } else {
            priceCompareEl.style.display = 'none';
            if (saveBadge) saveBadge.style.display = 'none';
          }
        }

        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        if (stickyActions) {
          var stickyIdInput = $('[data-tnr-sticky-variant-id]', stickyActions);
          if (stickyIdInput) stickyIdInput.value = variant.id;
        }

        var btn = $('button[type="submit"]', form);
        if (btn) {
          if (variant.available) {
            btn.removeAttribute('disabled');
            var span = $('span', btn);
            if (span) span.textContent = 'Add to cart';
          } else {
            btn.setAttribute('disabled', 'disabled');
            var span = $('span', btn);
            if (span) span.textContent = 'Sold out';
          }
        }
        
        var stickyBtn = stickyActions ? $('button[type="submit"]', stickyActions) : null;
        if (stickyBtn) {
           if (variant.available) {
            stickyBtn.removeAttribute('disabled');
            var span = $('span', stickyBtn);
            if (span) span.textContent = 'Add to cart';
          } else {
            stickyBtn.setAttribute('disabled', 'disabled');
            var span = $('span', stickyBtn);
            if (span) span.textContent = 'Sold out';
          }
        }

        if (variant.featured_image && variant.featured_image.src && galleryMain) {
           galleryMain.src = variant.featured_image.src;
        }
      }

      inputs.forEach(function (input) {
        input.addEventListener('change', function () {
           if (input.type === 'radio' && !input.checked) return;
           update(input.value);
        });
      });
    });
  }

  /* --- Boot ------------------------------------------------------------- */

  function init() {
    initStickyHeader();
    initMegaMenu();
    initAccordions();
    initDrawer();
    Cart.init();
    Cart.initForms();
    Cart.initCartPage();
    initPredictiveSearch();
    initStickyAtc();
    initCompare();
    initReveal();
    initPopup();
    initCarousels();
    initQuantity();
    initGalleries();
    initSortForms();
    initMobileSearch();
    initCartAnnouncer();
    initMarqueePause();
    initVariantSelectors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
